import React, { useState } from 'react';
import { PageLayout } from '../components/layout/Navbar';
import { Card, Button } from '../components/ui';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { paymentApi } from '../services/api';
import { toast } from 'sonner';

export default function PremiumPanel() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const handleActivate = async () => {
    if (user?.isPremium) {
      toast.info(t('premiumPanel.alreadyActive'));
      return;
    }
    setLoading(true);
    try {
      const res = await paymentApi.subscribePremium();
      if (res.status === 'SUCCESS') {
        toast.success(t('premiumPanel.success'));
        await refreshUser();
      } else {
        toast.error(t('premiumPanel.error'));
      }
    } catch (err) {
      toast.error(err.message || t("premiumPanel.insufficientBalance"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-black text-slate-900 dark:text-white mb-4">
            {t('premiumPanel.title')} <span className="text-amber-500">{t('premiumPanel.premium')}</span>
          </h1>
          <p className="text-slate-500 text-lg">{t('premiumPanel.subtitle')}</p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {[
            { 
              title: t('premiumPanel.fastQueue'), 
              desc: t('premiumPanel.fastQueueDesc'),
              icon: "bolt"
            },
            { 
              title: t('premiumPanel.discounts'), 
              desc: t('premiumPanel.discountsDesc'),
              icon: "percent"
            },
            { 
              title: t('premiumPanel.support'), 
              desc: t('premiumPanel.supportDesc'),
              icon: "support_agent"
            }
          ].map((feat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
            >
              <Card className="p-6 text-center hover:shadow-xl transition-shadow border-amber-100 dark:border-amber-900/30">
                <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="material-symbols-outlined text-amber-600 text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>{feat.icon}</span>
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">{feat.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{feat.desc}</p>
              </Card>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12"
        >
          <Card className="p-8 bg-slate-900 dark:bg-slate-800 text-white border-0 overflow-hidden relative">
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h2 className="text-3xl font-black mb-2">
                  {user?.isPremium ? t('premiumPanel.statusPremium') : t('premiumPanel.statusNotPremium')}
                </h2>
                <p className="text-slate-400">
                  {user?.isPremium ? t('premiumPanel.activePremium') : t('premiumPanel.pricePremium')}
                </p>
              </div>
              <Button 
                size="lg" 
                onClick={handleActivate}
                loading={loading}
                disabled={user?.isPremium}
                className={`font-black px-10 border-0 ${
                  user?.isPremium 
                    ? "bg-emerald-500 text-white cursor-default" 
                    : "bg-amber-500 hover:bg-amber-600 text-white"
                }`}
              >
                {user?.isPremium ? t('premiumPanel.btnActive') : t('premiumPanel.btnActivate')}
              </Button>
            </div>
            {/* Decoration */}
            <div className="absolute -right-10 -bottom-10 w-64 h-64 bg-amber-500/10 rounded-full pointer-events-none" />
          </Card>
        </motion.div>
      </div>
    </PageLayout>
  );
}
